import { durationStringToSeconds, formatDurationForDisplay, parseDate } from '@talos/kyoko';
import Big, { type BigSource } from 'big.js';
import type { ResubmitOrderSource } from '../../providers/orders.types';
import type { PreTradeResponseData } from './redux/preTradeApi';
import { PreTradeAnalyticProperty, type PreTradeAnalytic } from './redux/preTradeApiTypes';
import type { TotalCostRiskData } from './types';

const TOTAL_COST_RISK_PROPERTIES: PreTradeAnalytic['Property'][] = [
  PreTradeAnalyticProperty.SpreadCost,
  PreTradeAnalyticProperty.TemporaryCost,
  PreTradeAnalyticProperty.TimeRisk,
];

type StrategyDefinition = {
  DisplayName?: string;
};

export type StrategiesByName = Map<string, StrategyDefinition>;

/**
 * Parses a numeric string and returns `null` when the value is missing or not finite.
 */
export function parseFiniteNumber(value: string | undefined): number | null {
  if (value == null) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Computes a bar-width ratio clamped to the `[0, 1]` range.
 */
export function calculateRelativeBarWidth(value: BigSource, referenceValue: BigSource): number {
  try {
    const valueBig = Big(value);
    const referenceBig = Big(referenceValue);

    if (valueBig.gt(referenceBig)) {
      return 1;
    }
    if (referenceBig.eq(0)) {
      return 0;
    }

    const width = valueBig.div(referenceBig).toNumber();
    return Number.isFinite(width) ? Math.max(0, width) : 0;
  } catch {
    return 0;
  }
}

/**
 * Computes bar-width ratios for interval and daily duration columns.
 */
export function calculateDurationBarWidth(intervalDuration: string, dailyDuration: string, colId: string): number {
  const intervalSeconds = durationStringToSeconds(intervalDuration);
  const dailySeconds = durationStringToSeconds(dailyDuration);

  if (intervalSeconds == null || dailySeconds == null) {
    return 0;
  }

  if (colId === 'interval') {
    return calculateRelativeBarWidth(intervalSeconds, dailySeconds);
  }
  if (colId === 'daily') {
    return calculateRelativeBarWidth(dailySeconds, intervalSeconds);
  }
  return 0;
}

/**
 * Returns whether a NOS field is marked as considered in the response payload.
 */
export function isFieldConsidered(
  consideredFields: PreTradeResponseData['NOSConsideredFields'] | undefined,
  field: string
): boolean {
  if (!consideredFields?.length) {
    return false;
  }

  return consideredFields.some(consideredField => consideredField.Name === field);
}

/**
 * Maps a pre-trade scenario's NOS payload into a `ResubmitOrderSource` for OMS priming.
 *
 * We intentionally keep this minimal and trust backend payload shape:
 * - pass through all backend-provided NOS fields as-is
 * - backfill `DecisionStatus` from `InitialDecisionStatus` when needed
 */
export function mapPreTradeScenarioToResubmitOrder(scenario: PreTradeResponseData): ResubmitOrderSource {
  const nos = scenario.NewOrderSingle;
  return {
    ...nos,
    // Pre-trade scenario NOS carries InitialDecisionStatus; OMS priming consumes DecisionStatus.
    DecisionStatus: nos.DecisionStatus ?? nos.InitialDecisionStatus,
  };
}

/**
 * Extracts spread cost, temp cost, and risk from global analytics (Market == null).
 * Market-scoped analytics are intended for the Public Markets chart, not this one.
 * Returns undefined when any required property is missing or unparseable.
 */
export function parseTotalCostRisk(analytics: PreTradeAnalytic[]): TotalCostRiskData | undefined {
  const hasGlobal = (property: PreTradeAnalytic['Property']) =>
    analytics.some(a => a.Property === property && a.Market == null);
  if (!TOTAL_COST_RISK_PROPERTIES.every(p => hasGlobal(p))) {
    return undefined;
  }

  const getValue = (property: PreTradeAnalytic['Property']): number | undefined => {
    const analytic = analytics.find(a => a.Property === property && a.Market == null);
    if (!analytic) {
      return undefined;
    }

    const parsed = Number.parseFloat(analytic.Value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const spreadCost = getValue(PreTradeAnalyticProperty.SpreadCost);
  const tempCost = getValue(PreTradeAnalyticProperty.TemporaryCost);
  const risk = getValue(PreTradeAnalyticProperty.TimeRisk);
  if (spreadCost === undefined || tempCost === undefined || risk === undefined) {
    return undefined;
  }
  const total = spreadCost + tempCost + risk;

  return { spreadCost, tempCost, risk, total };
}

/**
 * Finds an analytic value by property, optionally scoped to a market.
 */
export function getAnalyticValue(analytics: PreTradeAnalytic[], property: string, market?: string): string | undefined {
  return analytics.find(analytic => {
    if (analytic.Property !== property) {
      return false;
    }
    if (market != null) {
      return analytic.Market === market;
    }
    return analytic.Market == null;
  })?.Value;
}

/**
 * Returns (numerator / denominator) * 100 as BigSource, or null when either is undefined or denominator is zero.
 * Used for "vs" column: interval as percentage of daily.
 */
export function calcRatioAsPercent(numerator?: number, denominator?: number): BigSource | null {
  if (numerator === undefined || denominator === undefined || denominator === 0) {
    return null;
  }
  return Big(numerator).div(denominator).times(100);
}

/**
 * Resolves a strategy display name with best-effort fallback to the original strategy name.
 */
export function formatStrategyDisplayName(
  strategyName: string | undefined,
  strategiesByName?: StrategiesByName
): string | undefined {
  const trimmedStrategyName = strategyName?.trim();
  if (!trimmedStrategyName) {
    return undefined;
  }

  const strategyDisplayName = strategiesByName?.get(trimmedStrategyName)?.DisplayName?.trim();
  if (strategyDisplayName) {
    return strategyDisplayName;
  }

  return trimmedStrategyName;
}

/**
 * Converts a positive number of seconds into a compact duration string
 * consumable by `durationStringToSeconds` (for example `1h`, `1h30m5s`).
 */
export function toCompactDurationString(totalSeconds: number): string | undefined {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return undefined;
  }

  const roundedSeconds = Math.round(totalSeconds);
  if (roundedSeconds <= 0) {
    return undefined;
  }

  const days = Math.floor(roundedSeconds / 86400);
  const hours = Math.floor((roundedSeconds % 86400) / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const seconds = roundedSeconds % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join('');
}

/**
 * Derives a duration string from ISO start/end timestamps.
 * Returns `undefined` when timestamps are missing, invalid, or non-increasing.
 */
export function deriveDurationFromTimestamps(startTime?: string, endTime?: string): string | undefined {
  if (!startTime || !endTime) {
    return undefined;
  }

  const startMs = parseDate(startTime).getTime();
  const endMs = parseDate(endTime).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return undefined;
  }

  return toCompactDurationString((endMs - startMs) / 1000);
}

/**
 * Formats a scenario name with duration metadata when present.
 */
export function formatScenarioName(scenario: PreTradeResponseData, strategiesByName: StrategiesByName): string {
  const duration =
    scenario.NewOrderSingle?.Parameters?.Duration || deriveDurationFromTimestamps(scenario.StartTime, scenario.EndTime);
  const strategyDisplayName =
    formatStrategyDisplayName(scenario.Strategy, strategiesByName) ?? scenario.Strategy.trim();

  const displayDuration = duration ? formatDurationForDisplay(duration) : undefined;
  return displayDuration ? `${strategyDisplayName} - ${displayDuration}` : strategyDisplayName;
}

/**
 * Formats a scenario label with version and scenario name.
 */
export function formatScenarioLabel(version: number, scenarioName: string): string {
  return `V${version} - ${scenarioName}`;
}

const BPS_DECIMAL_PLACES = 2;

/** Format a numeric BPS value to a fixed number of decimal places. */
export function formatCostRiskBps(value: number): string {
  return value.toFixed(BPS_DECIMAL_PLACES);
}

const MINUTES_DECIMAL_PLACES = 2;

/** Format a duration in minutes to a fixed number of decimal places (matches axis/tooltip/legend formatting). */
export function formatDurationMinutes(value: number): string {
  return value.toFixed(MINUTES_DECIMAL_PLACES);
}
